import { computed, ref, type Ref } from "vue";
import { unescape } from "lodash";

import type { Attribute, AttributesList } from "@/models/attribute";
import useConfig from "@/composables/config";

export interface GetAttributesRequest {
  domains: string;
  language: string;
}

export interface AttributesResponse {
  language_used: string;
  attributes: AttributesList;
}

// TODO port cache to backend.ts, or rely on browser-cache?

// Cache für die Attributes
const attributesCache: { [lang: string]: AttributesList } = {};
// Cache für die HTTP Requests für die Attributes
const requestCache: { [lang: string]: Promise<Response> } = {};

function encodeParams(req: GetAttributesRequest): string {
  const params = new URLSearchParams(req as unknown as Record<string, string>);
  return params.toString();
}

function fixEscapedEntities(attrList: AttributesList) {
  Object.values(attrList).forEach((v) => {
    v.forEach((a) => {
      a.help = unescape(a.help);
    });
  });
  return attrList;
}

export default function useGetAttributes() {
  const attrs: Ref<Record<string, Attribute[]>> = ref({});

  // Holt die geforderten Attribute und gibt sie als Objekt zurück
  function assembleAttributes(attributes: string[], lang: string) {
    return attributes.reduce(
      (p, c) => {
        p[c] = attributesCache[lang][c];
        return p;
      },
      {} as Record<string, Attribute[]>
    );
  }

  async function getAttributes(attributes: string[], lang: string) {
    if (!attributesCache[lang]) {
      attributesCache[lang] = {};
    }

    const missingAttributes = attributes.filter(
      (x) => !(Object.keys(attributesCache[lang]) ?? {}).includes(x)
    );

    // Alle Attribute im Cache, direkt zurückgeben
    if (missingAttributes.length === 0) {
      attrs.value = assembleAttributes(attributes, lang);
      return computed(() => attrs.value);
    }

    // Fehlende Attribute sortieren um stabile Keys für die Request-Cache
    // zu bekommen
    missingAttributes.sort();
    const domains = JSON.stringify(missingAttributes);
    const cacheKey = `${lang}:${domains}`;

    // Wenn es im Cache ein gleiches Request gibt diesen Verwenden und nicht
    // nochmal abschicken
    if (cacheKey in requestCache) {
      const cachedRequest = (await requestCache[cacheKey]).clone();
      const data = await cachedRequest.json();
      Object.assign(
        attributesCache[lang],
        fixEscapedEntities(data.attributes ?? {})
      );
      // Aus dem Cache entfernen, ist schon vorhanden
      delete requestCache[cacheKey];

      attrs.value = assembleAttributes(attributes, lang);
      return computed(() => attrs.value);
    }

    const requestData: GetAttributesRequest = {
      domains: domains,
      language: lang,
    };

    const { config } = useConfig();
    const queryString = `${
      config.backendPath
    }/jsonApi/v1/getAttributesArrayFromDomain?${encodeParams(requestData)}`;

    // Kein await hier, wollen den Request und nicht das Resultat speichern
    const request = fetch(queryString);
    requestCache[cacheKey] = request;

    const res = await request;
    const data = await res.clone().json();
    Object.assign(
      attributesCache[lang],
      fixEscapedEntities(data.attributes ?? {})
    );
    // Request verarbeitet, kann aus dem Cache raus
    delete requestCache[cacheKey];

    attrs.value = assembleAttributes(attributes, lang);
    return computed(() => attrs.value);
  }

  function getAttributeFieldName(attributeName: string) {
    switch (attributeName) {
      case "language":
        return "language";
      case "lang":
        return "lang";
      case "fachsigel":
        return "full_norm_id";
      default:
        return "type";
    }
  }

  return {
    getAttributes,
    attributes: computed(() => attrs.value),
    getAttributeFieldName,
  };
}
